import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Device } from '../types';
import { LOGGING_MODE_OPTIONS } from '../utils/loggingMode';
import { resolveOtaMode, OtaMode } from '../utils/otaMode';
import OtaModeSelect from './OtaModeSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faDownload, faBolt } from '@fortawesome/free-solid-svg-icons';
import SentryDropdown, { sentryContainsQuery } from './SentryDropdown';

// Minimum firmware version that can act on MQTT commands pushed from the server
// (e.g. the "Install firmware" OTA-by-URL push and the "Trigger alert" command).
// Devices below this silently ignore those commands, so the buttons are disabled.
const MIN_COMMAND_FIRMWARE = '2.7.0';

// Compares dotted version strings (ignoring a leading "v"). Returns 1, 0, or -1.
const compareVersions = (a: string, b: string): number => {
  const parse = (value: string) =>
    String(value ?? '')
      .replace(/^v/i, '')
      .split('.')
      .map((part) => parseInt(part, 10) || 0);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
};

interface DeviceRowProps {
  device: Device;
  userName: string;
  onToggleMarked: (serial: string, value: OtaMode) => void;
  onUpdateLoggingMode: (serial: string, mode: number) => void;
  onFirmwareInstall?: (device: Device) => void;
  onAlertsOpen?: (device: Device) => void;
  isUpdatingOTA?: boolean;
  showManagementControls?: boolean;
  showOwnerColumns?: boolean;
  connectivityPending?: boolean;
}

const DeviceRow: React.FC<DeviceRowProps> = ({ 
  device, 
  userName, 
  onToggleMarked,
  onUpdateLoggingMode,
  onFirmwareInstall,
  onAlertsOpen,
  isUpdatingOTA = false,
  showManagementControls = true,
  showOwnerColumns = true,
  connectivityPending = false
}) => {
  const location = useLocation();
  // Helper function to format the date
  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get badge color based on device type
  const getTypeBadgeClass = (type: string): string => {
    switch(type.toLowerCase()) {
      case 'dev':
        return 'bg-danger';
      case 'beta':
        return 'bg-warning text-dark';
      case 'prod':
        return 'bg-success';
      case 'deco':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const onlineTitle = device.mqttOnline
    ? `Online${device.mqttLastSeenAt ? ` since ${formatDate(device.mqttLastSeenAt)}` : ''}`
    : device.mqttOnline === false
      ? `Offline${device.mqttLastSeenAt ? `, last seen ${formatDate(device.mqttLastSeenAt)}` : ''}`
      : 'Online status unavailable';
  const statusColor =
    device.mqttOnline === true ? '#198754' : device.mqttOnline === false ? '#dc3545' : '#6c757d';
  const statusGlow =
    device.mqttOnline === true
      ? 'rgba(25, 135, 84, 0.18)'
      : device.mqttOnline === false
        ? 'rgba(220, 53, 69, 0.18)'
        : 'rgba(108, 117, 125, 0.18)';

  const userId =
    typeof device.user === 'object'
      ? device.user?._id
      : device.user || null;

  // Server-pushed MQTT commands only work on firmware >= MIN_COMMAND_FIRMWARE.
  const supportsRemoteCommands =
    compareVersions(device.firmware || '0.0.0', MIN_COMMAND_FIRMWARE) >= 0;
  const incompatibleTitle = `Requires firmware v${MIN_COMMAND_FIRMWARE}+ (device is ${device.firmware || 'unknown'})`;
  // Muted-but-still-colored look for disabled command buttons: keep each
  // button's own outline color (green/yellow), just faded out.
  const disabledButtonStyle: React.CSSProperties = {
    opacity: 0.45,
    cursor: 'not-allowed',
  };

  return (
    <tr>
      <td className="text-center" style={{ width: '28px' }}>
        {connectivityPending ? (
          <span
            className="spinner-border text-secondary"
            role="status"
            aria-label="Checking online status"
            style={{ width: '10px', height: '10px', borderWidth: '2px' }}
          />
        ) : (
          <span
            title={onlineTitle}
            aria-label={onlineTitle}
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: statusColor,
              boxShadow: `0 0 0 2px ${statusGlow}`,
            }}
          />
        )}
      </td>
      <td>{device.serial}</td>
      <td>                
        <span className={`badge ${
          device.model.includes('Fluora Mini') 
            ? 'bg-primary' 
            : device.model.includes('Fluora Floor') 
              ? 'bg-info'
              : device.model.includes('Monos')
                ? 'bg-warning'
                : 'bg-warning'
        }`}>
          {device.model}
        </span>
      </td>
      <td>{device.firmware || '—'}</td>
      <td>{device.hardware?.pcbRevision || '—'}</td>
      <td>
        <span className={`badge rounded-pill ${getTypeBadgeClass(device.deployment?.type || '')}`} style={{ minWidth: '60px' }}>
          {device.deployment?.type}
        </span>
      </td>
      {showOwnerColumns && (
        <>
          <td>{device.deployment?.group}</td>
          <td>
            {userId ? (
              <Link to={`/users/${userId}`}>{userName}</Link>
            ) : (
              userName
            )}
          </td>
        </>
      )}
      <td>{formatDate(device.stats?.lastCheckInTime)}</td>
      {showManagementControls && (
        <>
          <td>
            <select
              className="form-select form-select-sm"
              value={device.loggingMode ?? 0}
              onChange={(e) => onUpdateLoggingMode(device.serial, parseInt(e.target.value))}
              style={{ width: '150px' }}
            >
              {LOGGING_MODE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </td>
          <td>
            {isUpdatingOTA ? (
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
            ) : (
              <OtaModeSelect
                value={resolveOtaMode(device.deployment)}
                onChange={(mode) => onToggleMarked(device.serial, mode)}
              />
            )}
          </td>
          <td>
            <Link
              className="btn btn-sm btn-outline-primary me-2"
              to={`/device/${encodeURIComponent(device.serial)}?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
              title="Edit device"
            >
              <FontAwesomeIcon icon={faEdit} />
            </Link>
            {onFirmwareInstall && (
              <button
                className="btn btn-sm btn-outline-success me-2"
                onClick={() => onFirmwareInstall(device)}
                disabled={!supportsRemoteCommands}
                title={supportsRemoteCommands ? 'Install firmware' : incompatibleTitle}
                style={!supportsRemoteCommands ? disabledButtonStyle : undefined}
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
            )}
            {onAlertsOpen && (
              <button
                className="btn btn-sm btn-outline-warning me-2"
                onClick={() => onAlertsOpen(device)}
                disabled={!supportsRemoteCommands}
                title={supportsRemoteCommands ? 'Trigger alert' : incompatibleTitle}
                style={!supportsRemoteCommands ? disabledButtonStyle : undefined}
              >
                <FontAwesomeIcon icon={faBolt} />
              </button>
            )}
            <SentryDropdown
              issuesQuery={sentryContainsQuery('serial', device.serial)}
              logsFields={['timestamp', 'message', 'serial']}
            />
          </td>
        </>
      )}
    </tr>
  );
};

export default DeviceRow;